import React, { useCallback, useState, useEffect } from "react";
import { map, pick, toNumber } from "lodash";
import { ScorecardRow } from "@/features/scorecards/graphql/generated";
import { ExpandableRow } from "@/shared/ui/ExpandableRow";
import { Title } from "@/shared/ui/Title";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { ModalFileName } from "@/shared/modals";
import { ScoringInput } from "@/shared/ui/ScoringInput";
import { cn } from "@/shared/utils/shadcn";
import { useModal } from "@/shared/utils/hooks/useModal";
import { RichTextEditor } from "@/shared/widgets/RichTextEditor";

interface LeadershipSpan {
  left: string;
  right: string;
  type: "leadership_span";
  left_description?: string;
  right_description?: string;
}

interface AssessmentRowProps extends Omit<ScorecardRow, "__typename"> {
  score?: number;
  onScoreChange?: (score: number) => void;
  isExpanded?: boolean;
  onExpand?: (id?: number) => void;
  summary?: string;
  tagsData?: any;
  onNotesChange?: (notes?: string) => void;
  notesHeader?: React.ReactNode;
  spanScore?: [number, number];
  leadershipSpan?: LeadershipSpan;
  spanScoreExtremes?: [string, string];
  onSpanScoreChange?: (mainScore: number, spanScores: [number, number]) => void;
  variant?: "card" | "default" | "wisgpt";
  descriptionLimit?: number;
}

function AssessmentRowContent(props: {
  description?: string;
  leadershipSpan?: LeadershipSpan;
  spanScore?: [number, number];
  handleSpanScoreChange: (spanScores: [number, number]) => void;
  notes: string;
  notesHeader?: React.ReactNode;
  handleNotesChange: (value?: string) => void;
  handleCopyFromWisGPT: () => void;
  onShowDescription?: () => void;
  descriptionLimit?: number;
  variant?: "card" | "default" | "wisgpt";
  tagsData?: any;
}) {
  const {
    leadershipSpan,
    spanScore,
    handleSpanScoreChange,
    notes,
    handleNotesChange,
    variant,
    notesHeader,
    descriptionLimit,
    tagsData,
  } = props;

  const [openSpanScoreDescription] = useModal(
    ModalFileName.ASSESSMENT_ROW_DETAILS
  );

  // Span score was initially a single line input that mirrored the scores but it changed to 2 separate score input for RRA
  // This is why the code is not ideal here for displaying and changing the span scores
  return (
    <>
      {!!leadershipSpan && (
        <>
          {map(pick(leadershipSpan, ["left", "right"]), (label, index) => (
            <Row
              key={index}
              className="justify-between p-4 border-b border-gray-200"
            >
              <Row className="items-center">
                <Title as="p" className="font-semibold text-sm text-grey-700">
                  {label}
                </Title>
                {!!leadershipSpan[
                  `${index}_description` as keyof LeadershipSpan
                ] && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-blue-500 p-0 flex-row-reverse ml-6"
                    onClick={() =>
                      openSpanScoreDescription({
                        title: label,
                        description:
                          leadershipSpan[
                            `${index}_description` as keyof LeadershipSpan
                          ],
                      })
                    }
                  >
                    Read more
                  </Button>
                )}
              </Row>
              <ScoringInput
                value={spanScore?.[index === "left" ? 0 : 1]}
                onChange={(value) => {
                  if (index === "left") {
                    handleSpanScoreChange([value, spanScore?.[1] || 0]);
                  } else {
                    handleSpanScoreChange([spanScore?.[0] || 0, value]);
                  }
                }}
              />
            </Row>
          ))}
        </>
      )}
      <Column
        className={cn(
          "pb-4",
          variant === "default" && "bg-grey-100 px-4 rounded-lg pb-0"
        )}
      >
        {notesHeader}
        <RichTextEditor
          onChange={handleNotesChange}
          defaultValue={notes}
          placeholder="Add your assessment notes here..."
          showWisGptCopy={variant === "wisgpt"}
          limit={descriptionLimit || 500}
          alwaysShowToolbar={variant === "wisgpt"}
          editorClassName={cn(
            variant === "default" && "!min-h-0 !max-h-unset !pb-0"
          )}
          tagsData={tagsData}
        />
      </Column>
    </>
  );
}

function AssessmentRow(props: AssessmentRowProps) {
  const {
    title,
    icon,
    isExpanded,
    onExpand,
    description,
    score: externalScore,
    leadershipSpan,
    onScoreChange,
    onNotesChange,
    spanScore: externalSpanScore,
    onSpanScoreChange,
    notesHeader,
    summary,
    tagsData,
    variant = "default",
  } = props;

  // Use external notes if provided, otherwise manage state internally

  // Maintain internal score state for immediate feedback
  const [internalScore, setInternalScore] = useState<number | undefined>(
    externalScore
  );

  // Maintain internal span score state
  const [internalSpanScore, setInternalSpanScore] = useState<[number, number]>(
    externalSpanScore || ([] as any)
  );

  const [internalNotes, setInternalNotes] = useState<string>(summary || "");

  const [showDetailsModal] = useModal(ModalFileName.ASSESSMENT_ROW_DETAILS, {
    title,
    description,
    icon,
  });

  // Update internal state when external props change
  useEffect(() => {
    if (externalScore !== undefined) {
      setInternalScore(externalScore);
    }
  }, [externalScore]);

  useEffect(() => {
    if (externalSpanScore?.length && !internalSpanScore?.length) {
      setInternalSpanScore(externalSpanScore);
    }
  }, [externalSpanScore, internalSpanScore]);

  useEffect(() => {
    if (summary !== undefined) {
      setInternalNotes(summary);
    }
  }, [summary]);

  const score = internalScore;
  const spanScore = internalSpanScore;

  const handleScoreChange = useCallback(
    (newScore: number) => {
      // Update internal state immediately for responsive UI
      setInternalScore(newScore);
      // Propagate to parent if callback exists
      onScoreChange?.(newScore);
    },
    [onScoreChange]
  );

  const handleSpanScoreChange = useCallback(
    (newSpanScore: [number, number]) => {
      // Update internal state immediately
      setInternalSpanScore(newSpanScore);
      // Propagate to parent if callback exists
      onSpanScoreChange?.(toNumber(internalScore), newSpanScore);
    },
    [onSpanScoreChange, internalScore]
  );

  const handleNotesChange = useCallback(
    (value?: string) => {
      if (onNotesChange) {
        onNotesChange(value);
      }
    },
    [onNotesChange]
  );

  useEffect(() => {
    // Listen for the custom event from the AI chat component
    const handleWisGptCopy = (event: CustomEvent) => {
      const { content } = event.detail;
      if (content && isExpanded) {
        setInternalNotes(content);
        handleNotesChange(content);
      }
    };

    // Add event listener
    document.addEventListener(
      "wisgpt-copy-to-editor",
      handleWisGptCopy as EventListener
    );

    // Clean up
    return () => {
      document.removeEventListener(
        "wisgpt-copy-to-editor",
        handleWisGptCopy as EventListener
      );
    };
  }, [handleNotesChange, isExpanded]);
  const handleCopyFromWisGPT = useCallback(() => {
    // Find all WisGPT assistant messages in the DOM
    const wisGptMessages = document.querySelectorAll(
      ".wisgpt-message-assistant"
    );

    if (wisGptMessages.length === 0) {
      console.log("No WisGPT messages found");
      return;
    }

    // Get the last message
    const lastMessage = wisGptMessages[wisGptMessages.length - 1];

    // Extract the message content from the data-message attribute
    const messageContent = lastMessage.getAttribute("data-message");

    if (!messageContent) {
      console.log("No message content found");
      return;
    }
    // Update the notes
    setInternalNotes(messageContent);
    handleNotesChange(messageContent);
  }, [handleNotesChange]);

  const AssessmentRowTitleComponent = useCallback(
    ({ isExpanded }: { isExpanded: boolean }) => (
      <Row
        className={cn(
          "w-full justify-between items-center p-4",
          isExpanded && "border-b border-gray-200",
          variant === "default" && "px-0"
        )}
      >
        <Row className="items-center">
          <Title
            as="p"
            className={cn(
              "text-sm",
              variant === "default" && "text-md",
              isExpanded && "font-semibold"
            )}
          >
            {title}
          </Title>
          {variant === "wisgpt" && (
            <Icon
              icon="ChevronDown"
              size="sm"
              color="blue"
              className={`transition-transform duration-300 ml-2 ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-500 p-0 flex-row-reverse ml-6 group-hover:block hidden"
            onClick={() => showDetailsModal?.()}
          >
            Read more
          </Button>
        </Row>
        <ScoringInput value={score} onChange={handleScoreChange} />
      </Row>
    ),
    [title, score, handleScoreChange, variant]
  );

  // Content to be rendered inside the expandable row
  const content = (
    <AssessmentRowContent
      key={`${title}-${internalNotes?.length}`}
      description={description || ""}
      leadershipSpan={leadershipSpan}
      spanScore={spanScore}
      handleSpanScoreChange={handleSpanScoreChange}
      notes={internalNotes}
      handleNotesChange={handleNotesChange}
      handleCopyFromWisGPT={handleCopyFromWisGPT}
      notesHeader={notesHeader}
      variant={variant}
      tagsData={tagsData}
    />
  );

  if (variant === "wisgpt") {
    return (
      <Card>
        <ExpandableRow
          TitleComponent={AssessmentRowTitleComponent}
          className="border-none py-0 group"
          isExpanded={isExpanded}
          onExpand={onExpand}
          key={`${title}-${isExpanded}-${internalNotes}`}
        >
          {content}
        </ExpandableRow>
      </Card>
    );
  }

  return (
    <Column className="space-y-8 group">
      <AssessmentRowTitleComponent isExpanded />
      {content}
    </Column>
  );
}

export default AssessmentRow;
export { AssessmentRow };
